import React, { useState, useEffect, useRef, useCallback, UIEvent } from 'react';
import clsx from 'clsx';
import numeral from 'numeral';
import { MenuItem, Popover, Popper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getRegExpByKeyword } from '@marqvision/shared/src/utils';
import { stringSort } from '../utils';
import { IcoCheckOutline } from '../../assets';
import MDSCheckbox from '../Checkbox';
import MDSSortBy from '../SortBy';
import MDSSearch from '../Search';
import MDSTypography from '../Typography';
import MDSTheme from '../Theme';
import DropDownItem from './DropDownItem';

const useStyles = makeStyles(() => ({
  dropdownPopover: {
    marginTop: '4px',
    borderRadius: '8px',
    maxHeight: '480px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      margin: '6px 0',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: MDSTheme.palette.bluegray['300'],
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      borderRadius: '8px',
    },
  },
  dropDownWrap: {
    minWidth: '180px',
    color: MDSTheme.palette.bluegray[800],
    '& .dropItem': {
      display: 'flex',
      alignItems: 'center',
      height: '48px',
      paddingRight: '16px',
      '&.selectable:hover': {
        backgroundColor: MDSTheme.palette.blue[50],
      },
      '&.hasChild': {
        paddingRight: '4px',
      },
      '&.hasImage': {
        height: '72px',
      },
      '& .highlight': {
        ...MDSTheme.typography.medium,
        color: MDSTheme.palette.yellow['600'],
      },
      '&.dropSortBox': {
        padding: '0 8px 0 16px',
      },
      '& .lazyImage': {
        display: 'flex',
        alignItems: 'center',
        marginRight: '16px',
        marginLeft: '8px',
        backgroundColor: MDSTheme.palette.bluegray[200],
        '& img': {
          display: 'block',
          width: '48px',
          height: '48px',
          objectFit: 'cover',
          borderRadius: '2px',
        },
      },
      '&.disabled': {
        opacity: '0.4',
        cursor: 'initial',
        '&:hover': {
          backgroundColor: 'unset',
        },
      },
    },
    '& .dropCheckbox': {
      padding: 0,
      marginRight: '8px',
    },
    '& .expendButton': {
      marginLeft: 'auto',
    },
    '& .dropSearchBox': {
      padding: '8px 8px 0',
    },
    '& .selectIcon': {
      width: '16px',
      height: '16px',
      marginRight: '4px',
      verticalAlign: 'text-bottom',
      '& > path': {
        fill: MDSTheme.palette.blue['700'],
      },
    },
  },
  dropSortBy: {
    marginLeft: 'auto',
  },
  dropSortWrap: {
    boxShadow: '0px 8px 16px 0px #00000033',
    backgroundColor: MDSTheme.palette.white,
    borderRadius: '8px',
    marginTop: '4px',
    width: '160px',
  },
  dropSortItem: {
    display: 'flex',
    alignItems: 'center',
    height: '48px',
    paddingRight: '16px',
    '&:hover': {
      backgroundColor: MDSTheme.palette.blue[50],
    },
    '& .selectIcon': {
      width: '16px',
      height: '16px',
      marginRight: '4px',
      verticalAlign: 'text-bottom',
      '& > path': {
        fill: MDSTheme.palette.blue['700'],
      },
    },
  },
  sortMenuItemWrap: {
    position: 'relative',
  },
  dropSortBoxShadow: {
    width: '100%',
    height: '10px',
    position: 'absolute',
    zIndex: 9,
    background: `linear-gradient(180deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0.02) 40%, rgba(0,0,0,0) 100%)`,
  },
  sortMenuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    '&:hover': {
      background: MDSTheme.palette.white,
    },
  },
  readonlyMenuItem: {
    cursor: 'initial',
    '&:hover': {
      background: MDSTheme.palette.white,
    },
  },
}));

export interface IDropDownItem {
  label: string | React.ReactNode;
  value: unknown;
  imageUrl?: string;
  disabled?: boolean;
  className?: string;
  isFolded?: boolean;
  children?: IDropDownItem[];
  displayLabel?: string;
  help?: React.ReactNode;
  renderComponent?: (onClick: () => void, onClose: () => void) => React.ReactNode;
  onView?: () => Promise<string>;
  count?: number | null;
}

interface IDropDownMultiProps extends IDropDownProps {
  isMultiSelect: true;
  value: unknown[];
}

interface IDropDownSingleProps extends IDropDownProps {
  isMultiSelect?: false;
  value: unknown;
}

enum LanguageEnum {
  English = 'en',
  Korean = 'ko',
}

export interface IDropDownProps {
  isSearch?: boolean;
  totalAndSort?: boolean;
  sortFeatured?: boolean;

  /**
   * Select All 체크박스를 항상 숨깁니다.
   * 아이템 선택 시 Deselect All만 가능합니다.
   */
  hideSelectAll?: boolean;
  /**
   * Search 중인 경우 Select All 체크박스를 숨깁니다.
   * 아이템 선택 시 Deselect All만 가능합니다.
   */
  hiddenSearchAllClick?: boolean;
  isReturnSelectedItem?: boolean;
  /**
   * Select All 인 경우에도 -1을 반환하지 않고 선택된 아이템 리스트를 반환합니다.
   */
  value: unknown | unknown[];
  list: IDropDownItem[];
  disabled?: boolean;
  readOnly?: boolean;
  renderAnchorComponent: (
    value: ISelectValue | ISelectValue[],
    handleOpen: (event: React.SyntheticEvent) => void
  ) => React.ReactNode;
  onChange?: (value: IDropDownItem['value'] | IDropDownItem['value'][]) => void;
  width?: number | string;
  onScrollToBottom?: () => void;
  totalCount?: number;
  hideFeaturedSort?: boolean;
  sort?: SortEnum;
  onSort?: (sort: SortEnum) => void;
  search?: string;
  onSearch?: (search: string) => void;
  textLengthToShowTooltip?: number;
  locale?: LanguageEnum;
  sortList?: IDropDownSortItem[];
  sortTitleLabel?: string;
  isInfiniteScroll?: boolean;
  hideSort?: boolean;
  className?: string;
  isAnchorWidthMatched?: boolean;
}

export enum SortEnum {
  Asc = 'asc',
  Desc = 'desc',
  Featured = 'featured',
}

interface IDropDownSortItem {
  value: SortEnum;
  label: string;
}

const sortingMenuList = (locale: LanguageEnum): IDropDownSortItem[] => [
  { value: SortEnum.Featured, label: localizedLabel.featured[locale] },
  { value: SortEnum.Asc, label: localizedLabel.asc[locale] },
  { value: SortEnum.Desc, label: localizedLabel.desc[locale] },
];

export interface IDropItemContext {
  getSelectedStatus: (item: IDropDownItem) => [unknown[], boolean, boolean, boolean];
  getIsFolded: (item: IDropDownItem) => boolean;
  setFoldedList: React.Dispatch<React.SetStateAction<unknown[]>>;
  setSelectedValue: React.Dispatch<React.SetStateAction<unknown[]>>;
  isMultiSelect?: boolean;
  textLengthToShowTooltip?: number;
  searchText: string;
  close: (value?: unknown) => void;
  selectedValue: unknown[];
  list: IDropDownItem[];
}

interface IDropDownPopup {
  anchorEl?: Element | EventTarget;
  isOpen: boolean;
  onClose: () => void;
  maxWidth?: number | string;
}

export interface ISelectValue {
  value: string | number;
  label: string;
  displayLabel?: string;
}

const localizedLabel = {
  all: {
    [LanguageEnum.Korean]: (total: number) => `전체 ${numeral(total).format('0,0')}개`,
    [LanguageEnum.English]: (total: number) => `All (${numeral(total).format('0,0')})`,
  },
  allWithPaging: {
    [LanguageEnum.Korean]: `전체`,
    [LanguageEnum.English]: `All`,
  },
  searched: {
    [LanguageEnum.Korean]: (total: number) => `${numeral(total).format('0,0')}개 검색됨`,
    [LanguageEnum.English]: (total: number) => `Searched (${numeral(total).format('0,0')})`,
  },
  selected: {
    [LanguageEnum.Korean]: (total: number) => `${numeral(total).format('0,0')}개 선택됨`,
    [LanguageEnum.English]: (total: number) => `Selected (${numeral(total).format('0,0')})`,
  },
  featured: {
    [LanguageEnum.Korean]: '추천순',
    [LanguageEnum.English]: 'Featured',
  },
  asc: {
    [LanguageEnum.Korean]: 'ㄱ-ㅎ',
    [LanguageEnum.English]: 'A-Z',
  },
  desc: {
    [LanguageEnum.Korean]: 'ㅎ-ㄱ',
    [LanguageEnum.English]: 'Z-A',
  },
  search: {
    [LanguageEnum.Korean]: '검색',
    [LanguageEnum.English]: 'Search',
  },
};

const DropDownPopup = (props: (IDropDownMultiProps | IDropDownSingleProps) & IDropDownPopup): React.ReactNode => {
  const { isOpen, onClose, locale = LanguageEnum.English, hideFeaturedSort } = props;
  const classes = useStyles();

  const sortList = sortingMenuList(locale).filter((item) =>
    hideFeaturedSort ? item.value !== SortEnum.Featured : true
  );

  const [isSortOpen, setIsSortOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const isGroupList = props.list.some((item) => item.children);
  const [sort, setSort] = useState<SortEnum>(props.sort || SortEnum.Asc);
  const [displayList, setDisplayList] = useState<IDropDownItem[]>(props.list);
  const [selectedValue, setSelectedValue] = useState<unknown[]>([]);
  const [foldedList, setFoldedList] = useState<unknown[]>([]);
  const [allChildValue, setAllChildValue] = useState<unknown[]>([]);

  const popoverRef = useRef<HTMLElement>();
  const sortRef = useRef<HTMLElement>();
  const contentRef = useRef<HTMLDivElement>(null);

  const resetAllFoldedList = useCallback(() => {
    const arr: unknown[] = [];
    const loop = (items: IDropDownItem[]) => {
      items.forEach((item) => {
        if (item.isFolded) {
          arr.push(item.value);
        }
        if (item.children) {
          loop(item.children);
        }
      });
    };
    loop(props.list);
    setFoldedList(arr);
  }, [props.list]);

  const getAllChildValue = (item: IDropDownItem) => {
    const arr: unknown[] = [];

    const loop = (currentItem: IDropDownItem) => {
      if (!currentItem.children) {
        if (currentItem.value !== undefined) {
          arr.push(currentItem.value);
        }
      } else {
        currentItem.children.forEach((loopItem) => loop(loopItem));
      }
    };
    loop(item);

    return arr;
  };

  const renderSearchBox = (): React.ReactNode => {
    const handleSearch = (text: string) => {
      setSearchText(text);
      props.onSearch?.(text);
    };

    return (
      <div className="dropSearchBox">
        <MDSSearch
          autoFocus
          placeholder={localizedLabel.search[locale]}
          realtime
          onSearch={(value) => handleSearch(value)}
          style={{ width: '100%' }}
        />
      </div>
    );
  };

  const renderSortingMenu = (): React.ReactNode => {
    return (
      <Popper open={isSortOpen} anchorEl={sortRef.current} placement="bottom-start" style={{ zIndex: 1300 }}>
        <div className={classes.dropSortWrap}>
          {sortList
            .filter((sortItem) => isGroupList || props.onScrollToBottom || sortItem.value !== SortEnum.Featured)
            .map((item: IDropDownSortItem) => renderSortListItem(item))}
        </div>
      </Popper>
    );
  };

  const renderSortListItem = (item: IDropDownSortItem): React.ReactNode => {
    const isChecked = sort === item.value;

    const handleSortItemClick = (item: IDropDownSortItem) => {
      setSort(item.value);
      setIsSortOpen(false);
      props.onSort?.(item.value);
    };

    return (
      <MenuItem button onClick={() => handleSortItemClick(item)} className={classes.dropSortItem} key={item.value}>
        <MDSTypography
          variant="T14"
          weight={isChecked ? 'medium' : 'regular'}
          style={{
            color: isChecked ? MDSTheme.palette.blue['700'] : MDSTheme.palette.bluegray['800'],
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isChecked && <IcoCheckOutline className="selectIcon" />}
          {item.label}
        </MDSTypography>
      </MenuItem>
    );
  };

  const renderSortBox = (): React.ReactNode => {
    const displayedAllChild = (() => {
      const arr: unknown[] = [];
      const loop = (items: IDropDownItem[]) => {
        items.forEach((item) => {
          if (item.children) {
            loop(item.children);
          } else if (item.value) {
            arr.push(item.value);
          }
        });
      };
      loop(displayList);
      return arr;
    })();

    const handleAllClick = () => {
      if (selectedValue.length > 0) {
        setSelectedValue([]);
      } else if (!props.isReturnSelectedItem && props.isInfiniteScroll) {
        setSelectedValue([-1]);
      } else {
        setSelectedValue(displayedAllChild);
      }
    };

    const handleOpenSort = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      sortRef.current = event.currentTarget;
      setIsSortOpen((ps) => !ps);
    };

    const displayCheckbox =
      props.isMultiSelect &&
      !(selectedValue.length === 0 && props.hideSelectAll) && // Search 여부 무관 > Select All 막음
      !(searchText && selectedValue.length === 0 && props.hiddenSearchAllClick); // Search 했을때 > Select All 막음

    return (
      <>
        {props.isMultiSelect && (
          <div className="dropItem dropSortBox">
            <MenuItem
              onClick={() => displayCheckbox && handleAllClick()}
              className={clsx(classes.sortMenuItem, { [classes.readonlyMenuItem]: !displayCheckbox })}
            >
              {displayCheckbox && (
                <MDSCheckbox
                  className="dropCheckbox"
                  isPrimary
                  checked={selectedValue.length > 0 && allChildValue.length === selectedValue.length}
                  indeterminate={selectedValue.length > 0 && selectedValue.length !== allChildValue.length}
                />
              )}
              <MDSTypography weight="medium" variant="T14">
                {selectedValue.length === 0 &&
                  (searchText
                    ? localizedLabel.searched[locale](displayedAllChild.length)
                    : props.onScrollToBottom
                    ? localizedLabel.allWithPaging[locale]
                    : localizedLabel.all[locale](props.totalCount ? props.totalCount : displayedAllChild.length))}
                {selectedValue.length > 0 &&
                  localizedLabel.selected[locale](
                    props.onScrollToBottom && props.totalCount && props.list.length === selectedValue.length
                      ? props.totalCount
                      : selectedValue[0] === -1
                      ? props.totalCount || 0
                      : selectedValue.length
                  )}
              </MDSTypography>
            </MenuItem>
            {!props.hideSort &&
              (props.sortFeatured ? (
                <MDSSortBy label="Featured" isArrowIcon={false} className={classes.dropSortBy} />
              ) : (
                <MDSSortBy
                  onClick={handleOpenSort}
                  label={
                    (props.sortTitleLabel ? `${props.sortTitleLabel} ` : '') +
                    sortList.find((item: IDropDownSortItem) => item.value === sort)?.label
                  }
                  className={classes.dropSortBy}
                />
              ))}
          </div>
        )}
        {!props.isMultiSelect && (
          <div className="dropItem dropSortBox">
            <MDSTypography weight="medium" variant="T14">
              {searchText
                ? localizedLabel.searched[locale](displayedAllChild.length)
                : localizedLabel.all[locale](displayedAllChild.length)}
            </MDSTypography>
            <MDSSortBy
              onClick={handleOpenSort}
              label={
                (props.sortTitleLabel ? `${props.sortTitleLabel} ` : '') +
                sortList.find((item: IDropDownSortItem) => item.value === sort)?.label
              }
              className={classes.dropSortBy}
            />
          </div>
        )}
        {renderSortingMenu()}
      </>
    );
  };

  const getSelectedStatus = useCallback(
    (item: IDropDownItem): [unknown[], boolean, boolean, boolean] => {
      const allChildValue = getAllChildValue(item);
      const isSelected = !allChildValue.some((v) => !selectedValue.includes(v));
      const isIndeterminate = !isSelected && allChildValue.some((v) => selectedValue.includes(v));
      const isAllSelected = !isSelected && selectedValue && selectedValue.length > 0 && selectedValue[0] === -1;

      return [allChildValue, isSelected, isIndeterminate, isAllSelected];
    },
    [selectedValue]
  );

  const getIsFolded = (item: IDropDownItem): boolean => foldedList.includes(item.value);

  const handleClose = (value?: unknown) => {
    if (props.onChange && (props.isMultiSelect || value !== undefined)) {
      if (props.isMultiSelect) {
        // selected value가 [-1]인 경우 All Selected
        props.onChange(
          props.onScrollToBottom &&
            selectedValue.length > 0 &&
            selectedValue.length === props.list.length &&
            !props.isReturnSelectedItem &&
            !searchText
            ? [-1]
            : selectedValue
        );
      } else if (value !== undefined) {
        props.onChange(value);
      }
    }
    onClose();
    setIsSortOpen(false);
    props.onSort?.(
      !props.hideFeaturedSort && (isGroupList || props.onScrollToBottom) ? SortEnum.Featured : SortEnum.Asc
    );
    props.onSearch?.('');
    setAllChildValue(
      getAllChildValue({
        label: '',
        value: '',
        children: props.list,
      })
    );
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!props.onScrollToBottom || (props.totalCount && props.totalCount === props.list.length)) {
      return;
    }
    if (
      contentRef.current?.offsetHeight &&
      Math.ceil(event.currentTarget.clientHeight + event.currentTarget.scrollTop) >= contentRef.current.offsetHeight
    ) {
      props.onScrollToBottom();
    }
  };

  const getAllChildLabel = (item: IDropDownItem) => {
    const arr: string[] = [];

    const loop = (currentItem: IDropDownItem) => {
      if (typeof currentItem.label === 'string') {
        arr.push(currentItem.label);
      }
      if (currentItem.children) {
        currentItem.children.forEach((loopItem) => loop(loopItem));
      }
    };
    loop(item);

    return arr;
  };

  const getFilteredList = useCallback(
    (items: IDropDownItem[]) => {
      const loop = (currentItem: IDropDownItem): IDropDownItem[] => {
        const regex = getRegExpByKeyword(searchText);
        if (typeof currentItem.label === 'string' && currentItem.label.match(regex)) {
          return [currentItem];
        } else if (getAllChildLabel(currentItem).some((value) => value.match(regex))) {
          return [
            {
              ...currentItem,
              children: currentItem.children?.reduce(
                (totalArr: IDropDownItem[], value: IDropDownItem) => [...totalArr, ...loop(value)],
                []
              ),
            },
          ];
        } else {
          return [];
        }
      };
      if (searchText) {
        return items.reduce((totalArr: IDropDownItem[], value: IDropDownItem) => [...totalArr, ...loop(value)], []);
      }
      return items;
    },
    [searchText]
  );

  const getSortedList = useCallback(
    (items: IDropDownItem[]) => {
      const loop = (currentItems: IDropDownItem[]): IDropDownItem[] => {
        const newArr = [...currentItems];
        return newArr
          .sort((a: IDropDownItem, b: IDropDownItem) =>
            typeof a.label === 'string' && typeof b.label === 'string'
              ? stringSort(a.label, b.label, sort as 'asc' | 'desc')
              : 0
          )
          .map((v) => ({
            ...v,
            children: v.children ? loop(v.children) : undefined,
          }));
      };
      if (props.totalAndSort && sort !== SortEnum.Featured) {
        return loop(items);
      }
      return items;
    },
    [props.totalAndSort, sort]
  );

  useEffect(() => {
    setDisplayList(getFilteredList(props.list));
  }, [searchText, props.list, getFilteredList]);

  useEffect(() => {
    setDisplayList(props.hideSort ? props.list : getSortedList(props.list));
  }, [sort, props.list, props.hideSort, getSortedList]);

  useEffect(() => {
    if (isOpen) {
      setSelectedValue(
        Array.isArray(props.value)
          ? props.value.length === 1 && props.value[0] === -1
            ? allChildValue.length > 0
              ? allChildValue
              : props.value
            : props.value
          : [props.value]
      );
    }
  }, [allChildValue, isOpen, props.value]);

  return (
    <Popover
      open={isOpen}
      anchorEl={props.anchorEl as Element}
      classes={{ paper: classes.dropdownPopover }}
      onClose={() => handleClose()}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        onScroll: handleScroll,
        style: { width: props.width, maxWidth: props.maxWidth },
      }}
      ref={popoverRef}
    >
      <DropItemContext.Provider
        value={{
          list: props.list,
          getSelectedStatus,
          getIsFolded,
          setFoldedList,
          selectedValue,
          setSelectedValue,
          isMultiSelect: props.isMultiSelect,
          textLengthToShowTooltip: props.textLengthToShowTooltip,
          searchText,
          close: handleClose,
        }}
      >
        <div ref={contentRef} className={clsx(classes.dropDownWrap, props.className)}>
          {props.isSearch && renderSearchBox()}
          {props.totalAndSort && renderSortBox()}
          <div className={classes.sortMenuItemWrap}>
            {props.totalAndSort && <div className={classes.dropSortBoxShadow}></div>}
            {displayList.map((item: IDropDownItem) => (
              <DropDownItem
                key={item.value as string}
                item={item}
                depth={0}
                onClose={handleClose}
                initialValue={props.value}
              />
            ))}
          </div>
        </div>
      </DropItemContext.Provider>
    </Popover>
  );
};

export const DropItemContext = React.createContext<IDropItemContext>({} as IDropItemContext);

export const MDSDropDown = (props: IDropDownMultiProps | IDropDownSingleProps): React.ReactNode => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [anchorWidth, setAnchorWidth] = useState<number>();
  const anchorEl = useRef<EventTarget | Element>();

  const handleOpen = (event: React.SyntheticEvent) => {
    if (event && !props.disabled && !props.readOnly) {
      anchorEl.current = event.currentTarget;
      setIsOpen(true);

      if (props.isAnchorWidthMatched) {
        setAnchorWidth(event.currentTarget.clientWidth);
      }
    }
  };

  const getDropdownItems = (values: unknown[]): ISelectValue | ISelectValue[] => {
    const arr: ISelectValue[] = [];
    const loop = (items: IDropDownItem[]) => {
      items.forEach((item) => {
        if (item.children) {
          loop(item.children);
        } else if (values.includes(item.value) && typeof item.label === 'string') {
          arr.push({
            label: item.label,
            value: item.value as string | number,
            displayLabel: item.displayLabel,
          });
        }
      });
    };
    loop(props.list);
    return props.isMultiSelect ? arr : arr[0];
  };

  useEffect(() => {
    if (!props.onSearch && (props.disabled || props.readOnly)) {
      setIsOpen(false);
    }
  }, [props.onSearch, props.disabled, props.readOnly]);

  return (
    <>
      {props.renderAnchorComponent(
        getDropdownItems(Array.isArray(props.value) ? props.value : ([props.value] as unknown[])),
        handleOpen
      )}
      {isOpen && (
        <DropDownPopup
          {...props}
          anchorEl={anchorEl.current}
          width={anchorWidth}
          maxWidth={anchorWidth || props.width}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default MDSDropDown;
